import { PopoverContentProps } from "@radix-ui/react-popover";
import { CheckIcon, ChevronsUpDown } from "lucide-react";
import {
  Country,
  FlagProps,
  getCountryCallingCode,
} from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { useDisclosure } from "shared/lib/hooks";
import { clsxMerge } from "shared/lib/helpers";
import { Button } from "shared/ui/shadcn/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "shared/ui/shadcn/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "shared/ui/shadcn/popover";
import { ScrollArea } from "shared/ui/shadcn/scroll-area";

const POPOVER_ARIA_ID = "phone-input-field-country-select";

const MIN_COUNTRIES_TO_SHOW_SEARCH = 5;

const FlagComponent = ({ country, countryName }: FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="bg-foreground/20 flex h-4 w-5 overflow-hidden rounded-sm [&_svg]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
}: {
  selectedCountry: Country;
  onChange: (country: Country) => void;
} & FlagProps) => {
  return (
    <CommandItem className="gap-2" onSelect={() => onChange(country)}>
      <FlagComponent country={country} countryName={countryName} />

      <span className="flex-1 text-sm">{country}</span>
      <span className="text-foreground/50 text-sm">{`+${getCountryCallingCode(
        country
      )}`}</span>

      <CheckIcon
        className={`ml-auto size-4 ${
          country === selectedCountry ? "opacity-100" : "opacity-0"
        }`}
      />
    </CommandItem>
  );
};

interface CountrySelectPropsI {
  triggerClassName?: string;
  menuClassName?: string;
  disabled?: boolean;
  value: Country;
  options: { value: Country; label: string }[];
  onChange: (country: Country) => void;
  popoverContentProps?: PopoverContentProps;
}

export const CountrySelect = ({
  triggerClassName,
  menuClassName,
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
  popoverContentProps = {},
}: CountrySelectPropsI) => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Popover open={isOpen} onOpenChange={onToggle}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={POPOVER_ARIA_ID}
          className={clsxMerge(
            "mr-1 w-fit justify-between px-2.5",
            triggerClassName
          )}
          disabled={disabled}
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />
          <ChevronsUpDown
            className={clsxMerge(
              "-mr-1 size-4 opacity-50",
              disabled ? "hidden" : "opacity-100"
            )}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        id={POPOVER_ARIA_ID}
        className={clsxMerge(
          "pointer-events-auto w-fit border-2 p-0",
          menuClassName
        )}
        {...popoverContentProps}
      >
        <Command>
          {countryList.length >= MIN_COUNTRIES_TO_SHOW_SEARCH && (
            <CommandInput placeholder="Search" />
          )}
          <CommandList>
            <ScrollArea className="max-h-72">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                    />
                  ) : null
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
